/**
 * Optional audio.
 *
 * Two sources, deliberately:
 *
 *   Synthesised — the countdown pips and the result sting are generated with
 *   oscillators at runtime. No files, no downloads, and no copyright question
 *   to answer. These work out of the box.
 *
 *   Sampled — pen ambience and clucking need real recordings, which this
 *   repository does not ship. Drop your own into `public/audio/` (see
 *   `SAMPLES` below) and they are picked up automatically; a missing file is
 *   silently skipped rather than throwing.
 *
 * Nothing is created until the user's first gesture, so no browser ever has to
 * block us for autoplay, and the whole engine stays inert when muted.
 */

export type SampleName = 'ambience' | 'cluck' | 'gate';

/** Expected drop-in locations. Absent files are ignored. */
const SAMPLES: Record<SampleName, string> = {
  ambience: '/audio/pen-ambience.webm',
  cluck: '/audio/cluck.webm',
  gate: '/audio/gate.webm',
};

const MUTE_KEY = 'jeff.trade:muted:v1';

export class JeffAudio {
  #ctx: AudioContext | null = null;
  #master: GainNode | null = null;
  #buffers = new Map<SampleName, AudioBuffer | null>();
  #ambienceNode: AudioBufferSourceNode | null = null;
  #muted: boolean;

  constructor() {
    this.#muted = readMuted();
  }

  get muted(): boolean {
    return this.#muted;
  }

  /**
   * Must be called from inside a user-gesture handler. Idempotent, and safe to
   * call on a browser without Web Audio.
   */
  unlock(): void {
    if (this.#ctx || this.#muted) return;
    const Ctor: typeof AudioContext | undefined =
      globalThis.AudioContext ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;

    this.#ctx = new Ctor();
    this.#master = this.#ctx.createGain();
    this.#master.gain.value = 0.45;
    this.#master.connect(this.#ctx.destination);
    void this.#ctx.resume();
  }

  setMuted(muted: boolean): void {
    this.#muted = muted;
    writeMuted(muted);
    if (muted) {
      this.stopAmbience();
      void this.#ctx?.suspend();
    } else {
      this.unlock();
      void this.#ctx?.resume();
    }
  }

  /** A countdown pip. `final` raises the pitch for "1". */
  pip(final = false): void {
    this.#tone({
      frequency: final ? 880 : 587.33,
      durationMs: final ? 260 : 140,
      type: 'triangle',
      peak: final ? 0.5 : 0.32,
    });
  }

  /**
   * The result sting: a rising interval for LONG, a falling one for SHORT, so
   * the outcome is audible as well as visible.
   */
  sting(direction: 'LONG' | 'SHORT'): void {
    const notes = direction === 'LONG' ? [523.25, 659.25, 987.77] : [523.25, 415.3, 311.13];
    notes.forEach((frequency, i) => {
      this.#tone({ frequency, durationMs: 420, type: 'sine', peak: 0.34, delayMs: i * 110 });
    });
  }

  async startAmbience(): Promise<void> {
    if (this.#muted) return;
    this.unlock();
    const ctx = this.#ctx;
    const master = this.#master;
    if (!ctx || !master || this.#ambienceNode) return;

    const buffer = await this.#load('ambience');
    if (!buffer) return; // no asset shipped — silence is correct

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const gain = ctx.createGain();
    gain.gain.value = 0.3;
    source.connect(gain).connect(master);
    source.start();
    this.#ambienceNode = source;
  }

  stopAmbience(): void {
    try {
      this.#ambienceNode?.stop();
    } catch {
      /* already stopped */
    }
    this.#ambienceNode = null;
  }

  /** Slight pitch variation so repeats do not sound like a loop. */
  async cluck(): Promise<void> {
    await this.#playSample('cluck', 0.92 + Math.random() * 0.16);
  }

  /** The gate lifting, at the top of the countdown. */
  async gate(): Promise<void> {
    await this.#playSample('gate', 1);
  }

  async #playSample(name: SampleName, playbackRate: number): Promise<void> {
    if (this.#muted) return;
    this.unlock();
    const ctx = this.#ctx;
    const master = this.#master;
    if (!ctx || !master) return;

    const buffer = await this.#load(name);
    if (!buffer) return; // no asset shipped — silence is correct

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = playbackRate;
    source.connect(master);
    source.start();
  }

  dispose(): void {
    this.stopAmbience();
    void this.#ctx?.close();
    this.#ctx = null;
    this.#master = null;
  }

  #tone({
    frequency,
    durationMs,
    type,
    peak,
    delayMs = 0,
  }: {
    frequency: number;
    durationMs: number;
    type: OscillatorType;
    peak: number;
    delayMs?: number;
  }): void {
    if (this.#muted) return;
    this.unlock();
    const ctx = this.#ctx;
    const master = this.#master;
    if (!ctx || !master) return;

    const start = ctx.currentTime + delayMs / 1000;
    const end = start + durationMs / 1000;

    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.value = frequency;

    // A short attack and an exponential tail; a raw gate would click.
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(peak, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);

    osc.connect(gain).connect(master);
    osc.start(start);
    osc.stop(end + 0.02);
  }

  async #load(name: SampleName): Promise<AudioBuffer | null> {
    if (this.#buffers.has(name)) return this.#buffers.get(name) ?? null;
    const ctx = this.#ctx;
    if (!ctx) return null;

    try {
      const response = await fetch(SAMPLES[name]);
      if (!response.ok) throw new Error(`missing sample: ${name}`);
      const buffer = await ctx.decodeAudioData(await response.arrayBuffer());
      this.#buffers.set(name, buffer);
      return buffer;
    } catch {
      this.#buffers.set(name, null); // remember the absence; do not retry
      return null;
    }
  }
}

function readMuted(): boolean {
  try {
    // Default to muted. Sound on a link someone opened from Discord is rude.
    return localStorage.getItem(MUTE_KEY) !== 'false';
  } catch {
    return true;
  }
}

function writeMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, String(muted));
  } catch {
    /* ignore */
  }
}
